import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlayingCardFace } from "./PlayingCardFace";

type Props = {
  tapsDone: number;
  /** When true (e.g. not trailing team — should not happen if parent guards), no interaction */
  disabled: boolean;
  onCommitTap: () => void;
  deadlineMs: number | null;
  /** If set (e.g. /demo), show a dismiss control — not used in live ranked play. */
  onDismiss?: () => void;
};

export type CardScatterPos = { leftPct: number; topPct: number };

function inPileZone(p: CardScatterPos): boolean {
  return p.leftPct >= 36 && p.leftPct <= 64 && p.topPct >= 40 && p.topPct <= 62;
}

function distance2(a: CardScatterPos, b: CardScatterPos): number {
  const dx = a.leftPct - b.leftPct;
  const dy = a.topPct - b.topPct;
  return dx * dx + dy * dy;
}

/** Three spread-out positions (% of viewport) avoiding the center pile. */
export function genRandomCardPositions(): [CardScatterPos, CardScatterPos, CardScatterPos] {
  const MIN_D2 = 12 * 12;
  const out: CardScatterPos[] = [];
  const fallback: [CardScatterPos, CardScatterPos, CardScatterPos] = [
    { leftPct: 14, topPct: 20 },
    { leftPct: 86, topPct: 24 },
    { leftPct: 22, topPct: 82 },
  ];
  for (let n = 0; n < 3; n++) {
    let placed = false;
    for (let att = 0; att < 400; att++) {
      const leftPct = 7 + Math.random() * 86;
      const topPct = 10 + Math.random() * 78;
      const candidate = { leftPct, topPct };
      if (inPileZone(candidate)) continue;
      if (out.some((p) => distance2(p, candidate) < MIN_D2)) continue;
      out.push(candidate);
      placed = true;
      break;
    }
    if (!placed) {
      out.push(fallback[n]);
    }
  }
  return [out[0], out[1], out[2]];
}

/** Trailing-team ritual: three card backs at random screen spots → drag or tap-tap onto center pile. */
export function TrailingShuffleDealIntro({ tapsDone, disabled, onCommitTap, deadlineMs, onDismiss }: Props) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const cardW = isNarrow ? 64 : 76;
  const pileW = isNarrow ? 172 : 132;
  const pileH = Math.round(pileW * 1.15);

  const remaining = Math.max(0, 3 - tapsDone);
  const [pickedSlot, setPickedSlot] = useState<number | null>(null);
  const [burst, setBurst] = useState(0);
  const prevTaps = useRef(tapsDone);

  const positions = useMemo(() => genRandomCardPositions(), []);

  useEffect(() => {
    if (tapsDone > prevTaps.current) {
      setBurst((b) => b + 1);
      setPickedSlot(null);
    }
    prevTaps.current = tapsDone;
  }, [tapsDone]);

  const pileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled || remaining <= 0) return;
      onCommitTap();
    },
    [disabled, remaining, onCommitTap]
  );

  const pileClick = useCallback(() => {
    if (disabled || remaining <= 0) return;
    if (pickedSlot === null) return;
    onCommitTap();
    setPickedSlot(null);
  }, [disabled, remaining, onCommitTap, pickedSlot]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        backgroundColor: "rgba(0,0,0,0.82)",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {/* Top strip: copy + dismiss — stays above scatter; safe-area for notched phones */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          zIndex: 1210,
          pt: "max(10px, env(safe-area-inset-top))",
          px: 2,
          pb: 1,
          pointerEvents: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          background: "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", width: "100%", gap: 1, maxWidth: 560 }}>
          <Box sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1rem", sm: "1.1rem" } }}>
              Shuffle ritual
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: { xs: "0.72rem", sm: "0.8rem" }, lineHeight: 1.4, mt: 0.25 }}>
              Drag a card to the pile, or tap a card then tap the pile (best on phones). Three drops each.
            </Typography>
            {deadlineMs != null && (
              <Typography sx={{ color: "rgba(255,193,7,0.88)", fontSize: "0.68rem", mt: 0.35 }}>
                Deadline {new Date(deadlineMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </Typography>
            )}
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.75rem", mt: 0.35 }}>
              Your drops: {tapsDone} / 3
            </Typography>
          </Box>
          {onDismiss && (
            <IconButton
              onClick={onDismiss}
              aria-label="Dismiss"
              size="large"
              sx={{
                flexShrink: 0,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,255,255,0.28)",
                minWidth: 48,
                minHeight: 48,
                mt: 0.25,
                "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Center pile — large touch target on mobile */}
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        }}
        onDrop={pileDrop}
        onClick={pileClick}
        sx={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: pileW,
          height: pileH,
          borderRadius: 2,
          border: "2px dashed rgba(129, 199, 132, 0.55)",
          bgcolor: "rgba(76,175,80,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: remaining > 0 ? "pointer" : "default",
          zIndex: 1205,
          pointerEvents: "auto",
          transition: "border-color 0.2s, background-color 0.2s",
          boxShadow: pickedSlot !== null && remaining > 0 ? "0 0 0 3px rgba(255,215,0,0.35)" : "none",
          "&:hover": {
            borderColor: remaining > 0 ? "rgba(129, 199, 132, 0.95)" : undefined,
            bgcolor: remaining > 0 ? "rgba(76,175,80,0.14)" : undefined,
          },
        }}
      >
        <Box sx={{ position: "relative", width: cardW, height: Math.round(cardW * 1.43) }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                left: i * 3,
                top: i * 2,
                opacity: i < tapsDone ? 1 : 0.22,
                transform: i < tapsDone ? "none" : "scale(0.9)",
              }}
            >
              <PlayingCardFace rankCode="A" suitCode="S" back widthPx={Math.max(44, cardW - i * 4)} />
            </Box>
          ))}
        </Box>
      </Box>

      <AnimatePresence>
        {burst > 0 && (
          <motion.div
            key={burst}
            initial={{ opacity: 0.85, scale: 0.92 }}
            animate={{ opacity: 0, scale: 1.35 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            style={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 220,
              height: 220,
              borderRadius: "50%",
              border: "3px solid rgba(255,193,7,0.45)",
              pointerEvents: "none",
              zIndex: 1203,
            }}
          />
        )}
      </AnimatePresence>

      {remaining > 0 &&
        Array.from({ length: remaining }, (_, i) => {
          const pos = positions[tapsDone + i];
          const slotId = i;
          const isPicked = pickedSlot === slotId;
          return (
            <Box
              key={`${tapsDone}-${i}-${pos.leftPct}-${pos.topPct}`}
              draggable={!disabled && remaining > 0}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", "card");
                e.dataTransfer.effectAllowed = "move";
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (disabled || remaining <= 0) return;
                setPickedSlot((p) => (p === slotId ? null : slotId));
              }}
              sx={{
                position: "fixed",
                left: `${pos.leftPct}%`,
                top: `${pos.topPct}%`,
                transform: "translate(-50%, -50%)",
                zIndex: 1208,
                cursor: disabled ? "default" : "grab",
                outline: isPicked ? "3px solid rgba(255,215,0,0.9)" : "none",
                outlineOffset: 8,
                borderRadius: 2,
                pointerEvents: "auto",
                padding: "12px",
                margin: "-12px",
                touchAction: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
              }}
            >
              <PlayingCardFace rankCode="K" suitCode="S" back widthPx={cardW} />
            </Box>
          );
        })}
    </Box>
  );
}
